/**
 * Home — the table's landing surface.
 *
 * This is the screen most likely to be opened for pleasure rather than for a
 * task, so it is a shelf of recent nights and not a dashboard. Four things have
 * to be true on it at once:
 *
 *  - An unfinished night outranks everything else. A DRAFT session is a room
 *    full of people waiting, so it sits above the feed and above the primary
 *    action rather than politely below them.
 *  - The primary action is unmissable but not a banner. It is a round `+` pinned
 *    to the bottom right, made of the same lit gem the rating control uses, so it
 *    stays reachable down the feed without ever being a row the group scrolls
 *    past.
 *  - Nothing numeric is invented here. Every figure on this screen comes out of
 *    `data.ts` and is rendered by `Headline`, which cannot draw a number without
 *    its `n`. There is no overall score, no aspect summary on a tile, and no
 *    mean computed at this call site.
 *  - An empty shelf says so. No skeleton games, no sample data — the blank state
 *    is a sentence and a way into the first session.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";

import { DeleteNight } from "../../components/DeleteNight.tsx";
import { Tile } from "../../components/Tile.tsx";
import { Link } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import type { Game, PlaySession, SessionParticipant } from "../../../shared/types.ts";
import {
  Attribution,
  BoxArt,
  EmptyState,
  ScorePair,
  PlayerChips,
  ScreenHeader,
  SyncLine,
} from "./components.tsx";
import {
  blankShelf,
  byRecency,
  draftSessions,
  nLabel,
  relativeDay,
  reviewed,
  summariseGames,
  type GameSummary,
  type Library,
} from "./data.ts";
import { useLibrary } from "./useLibrary.ts";

import "./home.css";

/**
 * The feed is a landing surface, not the shelf. Past a dozen nights the answer
 * to "what have we been playing" stops changing, and the whole catalog is one
 * tap away.
 */
const FEED_LIMIT = 12;

export function Home(_props: ScreenProps): ReactNode {
  const { library, status, error, reload } = useLibrary();

  const games = useMemo(() => summariseGames(library), [library]);
  const feed = useMemo(() => byRecency(reviewed(games)).slice(0, FEED_LIMIT), [games]);
  const drafts = useMemo(() => draftRows(library), [library]);

  const loading = status === "loading";
  const blank = blankShelf({
    ready: status === "ready",
    reviewed: feed.length,
    drafts: drafts.length,
  });

  return (
    <div className="pb-home">
      <ScreenHeader title="Punchboard" />
      <SyncLine />

      {status === "error" && (
        <Tile edge="rust" title="The local store did not open">
          <p className="pb-home__error">{error ?? "Unknown error."}</p>
          <button type="button" className="pb-btn" onClick={reload}>
            Try again
          </button>
        </Tile>
      )}

      {drafts.length > 0 && (
        <section className="pb-section" aria-labelledby="pb-home-drafts">
          <h2 className="pb-section__title" id="pb-home-drafts">
            <span>Still being rated</span>
            <span className="pb-section__note">Pick it back up</span>
          </h2>
          <div className="pb-home__list">
            {drafts.map((row) => (
              <DraftTile key={row.session.id} row={row} />
            ))}
          </div>
        </section>
      )}

      {/* One way into a session at a time: on a blank shelf the empty state
          carries it, so the + would only be the same offer twice. It is fixed
          to the viewport, so where it sits in this tree changes nothing but the
          tab order — after the drafts, which still outrank it. */}
      {!blank && <StartAction />}

      {loading && <p className="pb-home__loading">Reading the shelf…</p>}

      {blank && (
        <EmptyState
          title="Nothing reviewed yet"
          action={
            <Link to="/setup" className="pb-home__cta">
              Rate your first game
            </Link>
          }
        >
          <p className="pb-home__blank-body">
            Punchboard fills up after you play. Finish a game, hand the phone around the table, and
            the night lands here — with what everyone actually said, not an average of it.
          </p>
        </EmptyState>
      )}

      {feed.length > 0 && (
        <section className="pb-section" aria-labelledby="pb-home-feed">
          <h2 className="pb-section__title" id="pb-home-feed">
            <span>Lately at this table</span>
            <span className="pb-section__note">Newest night first</span>
          </h2>
          <div className="pb-home__list">
            {feed.map((summary, index) => (
              <GameTile key={summary.game.id} summary={summary} hero={index === 0} />
            ))}
          </div>
        </section>
      )}

      {games.length > 0 && (
        <Link to="/catalog" className="pb-home__more">
          Open the shelf · {games.length} {games.length === 1 ? "game" : "games"} →
        </Link>
      )}

      <Attribution />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The primary action
// ---------------------------------------------------------------------------

/**
 * Setup lives at `/setup`, not `/session/new`: the latter is swallowed by
 * `/session/:id` and resolves to the rating screen with `params.id === "new"`.
 * Keeping it a link means the action survives a reload and can be shared to a
 * second device at the table.
 *
 * A round `+` pinned to the bottom right rather than a tile in the flow. The
 * plus is the whole label, so the accessible name has to be said out loud —
 * `aria-label` on the link, and the glyph hidden from the tree so a screen
 * reader is not handed "plus" twice.
 *
 * Fixed to the viewport, but `.pb-app` is a 34rem column centred in it, so a
 * plain `right` would strand the button in the margin of a wide window. The
 * offset in `home.css` follows the column's edge instead; the screen's own
 * bottom padding keeps the last row clear of it.
 */
function StartAction(): ReactNode {
  return (
    <Link to="/setup" className="pb-home__start" aria-label="Rate a night">
      <span aria-hidden="true">+</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

function GameTile({ summary, hero }: { summary: GameSummary; hero: boolean }): ReactNode {
  const { game, headline, overall, lastPlayedAt, plays, latestVerdict } = summary;

  return (
    <Tile
      as={Link}
      to={`/game/${game.id}`}
      className={hero ? "pb-home__game pb-home__game--hero" : "pb-home__game"}
    >
      {/* Only the first tile is above the fold, so only it loads eagerly. */}
      <BoxArt game={game} size={hero ? 96 : 68} eager={hero} />

      <div className="pb-home__game-body">
        <h3 className="pb-home__game-title">
          {game.title}
          {game.year !== null && <span className="pb-home__game-year"> {game.year}</span>}
        </h3>

        <ScorePair
          headline={headline}
          overall={overall}
          unit="night"
          size={hero ? "hero" : "normal"}
          absent="Played, but nobody put a number on it"
        />

        {latestVerdict && <p className="pb-home__verdict">{latestVerdict}</p>}

        <p className="pb-home__game-meta">
          {lastPlayedAt === null ? "Not played yet" : relativeDay(lastPlayedAt)}
          {/* With no headline there is no `n` on screen, so the count of nights
              has to be stated here instead of implied. */}
          {headline === null && <> · {nLabel(plays, "night")}</>}
        </p>
      </div>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

interface DraftRow {
  session: PlaySession;
  /** Null when the game row has not arrived (or was deleted) on this device. */
  game: Game | null;
  players: SessionParticipant[];
  /** How many people have committed a headline so far. Never a score. */
  rated: number;
}

/**
 * Drafts carry no aggregate, deliberately: a night where two of five people
 * have typed a number is half-finished, not a weak signal. What the screen owes
 * the group is progress — who is still holding the phone — so that is all it
 * shows.
 */
function draftRows(lib: Library): DraftRow[] {
  const drafts = draftSessions(lib);
  if (drafts.length === 0) return [];

  const games = new Map(lib.games.filter((g) => g.deleted !== 1).map((g) => [g.id, g] as const));
  const participants = lib.participants.filter((p) => p.deleted !== 1);
  const headlines = lib.headlines.filter((h) => h.deleted !== 1);

  return drafts.map((session) => ({
    session,
    game: games.get(session.game_id) ?? null,
    players: participants
      .filter((p) => p.session_id === session.id)
      .sort((a, b) => a.updated_seq - b.updated_seq),
    rated: new Set(
      headlines.filter((h) => h.session_id === session.id).map((h) => h.participant_id),
    ).size,
  }));
}

function DraftTile({ row }: { row: DraftRow }): ReactNode {
  const { session, game, players, rated } = row;

  return (
    <div className="pb-nightcard">
      <DraftLink row={row} />
      {/* No `onDeleted`: this list is driven by the local store, and the sync
          that `cancelSession` waits on removes the row from under it. Sending
          the screen anywhere would be sending it away from itself. */}
      <DeleteNight sessionId={session.id} placement="corner" />
    </div>
  );
}

function DraftLink({ row }: { row: DraftRow }): ReactNode {
  const { session, game, players, rated } = row;
  // One pip per person, and a rater who is no longer seated still counts.
  const total = Math.max(players.length, rated);

  return (
    // The tile used to end in "Resume ›", which was the only place the action
    // was named. Now that the whole tile is the tap and the corner is an ×, the
    // name has to be stated: without it the link announces as its own contents,
    // which is a game title and a pip count and no verb at all.
    <Tile
      as={Link}
      to={`/session/${session.id}`}
      className="pb-home__draft"
      aria-label={`Resume the unfinished ${game?.title ?? "Unnamed game"} night`}
    >
      {/* The slot is filled either way, so a draft whose game row has not synced
          yet does not shift the tile's columns. */}
      {game ? (
        <BoxArt game={game} size={52} />
      ) : (
        <span className="pb-home__draft-blank" aria-hidden="true" />
      )}

      <div className="pb-home__draft-body">
        <div className="pb-home__draft-head">
          <h3 className="pb-home__draft-title">{game?.title ?? "Unnamed game"}</h3>
          <span className="pb-badge" data-tone="pending">
            Unfinished
          </span>
        </div>

        <p className="pb-home__draft-meta">
          {relativeDay(session.played_at)}
          {total > 0 && <> · {rated} of {total} rated</>}
        </p>

        {total > 0 && (
          <div
            className="pb-home__progress"
            role="img"
            aria-label={`${rated} of ${total} people have rated`}
          >
            {Array.from({ length: total }, (_, i) => (
              <span className="pb-home__pip" data-filled={i < rated ? "true" : undefined} key={i} />
            ))}
          </div>
        )}

        {players.length > 0 && <PlayerChips players={players} />}
      </div>
    </Tile>
  );
}
